import { DownOutlined } from '@ant-design/icons';
import useVisibilitySensor from '@rooks/use-visibility-sensor';
import { Drawer, Dropdown, MenuProps } from 'antd';
import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';
import { Avatar, Header, Icon, Nav, Navbar } from 'rsuite';
import styled from 'styled-components';

import LanguageSwitcher from '../../../components/LanguageSwitcher/LanguageSwitcher';
import { WidthWrapper } from '../../../components/Layout/styles';
import { Logo } from '../../../components/Logo/Logo';
import { gtm } from '../../../reportGTM';
import { navigateLink } from '../../../utils/navigator.utils';
import { useAuth } from '../../Auth/AuthContext';
import { useSignOut } from '../../Auth/SignOut/useSignOut';
import { AppRoutes } from '../../Routing/routing.model';
import { useOnAddOfferPush } from '../hooks/onAddOfferPush.hook';

import { SPECIAL_OFFER_KEY } from './SpecialOffer100';

const StyledNavbarHeader = styled.div`
  float: left;
  @media (max-width: 415px) {
    margin-top: 20px;
  }
`;

const StyledNavbar = styled(Navbar)<{ sticky: boolean }>`
  padding-top: 35px;
  transition: all 0.3s ease;
  position: absolute;
  top: 0;
  width: 100%;
  box-shadow: none;
  * && {
    background-color: ${({ theme }) => theme.primary};
  }

  ${({ sticky }) =>
    sticky &&
    `
      box-shadow: 0 5px 30px rgb(0 22 84 / 10%);
      padding-top: 8px;
      padding-bottom: 8px;
      position: fixed;
      z-index: 10;
      * && {
        background-color: white;
      };
    `}
`;
const StyledNavSensor = styled.div<{ sticky?: boolean }>`
  height: 88px;
  background-color: ${({ theme }) => theme.primary};
  transition: all 0.3s ease;
  ${({ sticky }) =>
    sticky &&
    `
      height: 72px;
      background-color: white;
    `}
`;
const MenuButton = styled.div<{ sticky?: boolean }>`
  display: none;

  @media (max-width: 900px) {
    margin-top: 30px;
    display: block;
    position: absolute;
    top: 0;
    right: 0;
  }
  @media (max-width: 415px) {
    margin-top: 50px;
  }

  ${({ sticky }) =>
    sticky &&
    `
    @media (max-width: 415px) {
      margin-top: 20px;
    }
    `}
`;

const MobileNavItem = styled(Nav.Item)`
  margin-bottom: 16px !important;
  padding: 12px 16px !important;
  border-radius: 8px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f5f5f5;
  }

  &:last-child {
    margin-bottom: 0 !important;
  }
`;

export const FixedMenu: React.FC = () => {
  const { isAuth, showSignInModal, showSignUpModal, currentUser } = useAuth();
  const onSignOut = useSignOut();
  const { t } = useTranslation();
  const { push } = useHistory();
  const [activeKay, setActiveKey] = useState();
  const [mojaTabliceRedirect, setMojaTabliceRedirect] = useState(AppRoutes.MOJA_TABLICA);
  const { pathname } = useLocation();
  const [, setPromo] = useCookies(['specialOffer100']);

  const rootNode = useRef(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
  const [drawerVisible, setDrawerVisible] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 900);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const { isVisible } = useVisibilitySensor(rootNode, {
    intervalCheck: false,
    scrollCheck: true,
    resizeCheck: false,
    partialVisibility: true,
  });

  const onAddOfferPush = useOnAddOfferPush();

  const onAddOffer = useCallback(() => {
    if (!isAuth) {
      showSignInModal({
        onSuccess: () => Promise.resolve().then(() => onAddOfferPush()),
      });
    } else {
      onAddOfferPush();
    }
  }, [isAuth, showSignInModal, onAddOfferPush]);

  const items: MenuProps['items'] = [
    {
      label: <a href={navigateLink('/offers?premisesPurpose=Biurowe')}>{t('OFFICES')}</a>,
      key: '0',
    },
    {
      label: <a href={navigateLink('/offers?premisesPurpose=Handlowo-Usługowe')}>{t('RETAIL_SPACES')}</a>,
      key: '1',
    },
    {
      label: <a href={navigateLink('/offers?premisesPurpose=Magazynowo-Przemysłowe')}>{t('WAREHOUSES')}</a>,
      key: '2',
    },
  ];

  useEffect(() => {
    if (currentUser) {
      if (!currentUser.emailVerified) {
        setMojaTabliceRedirect(AppRoutes.MOJA_TABLICA_ACCOUNT_INFO);
      } else {
        setMojaTabliceRedirect(AppRoutes.MOJA_TABLICA);
      }
    }
  }, [currentUser]);

  const handleClickMojaTablica = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    push(navigateLink(mojaTabliceRedirect));
  };

  return (
    <Header>
      <StyledNavSensor ref={rootNode} sticky={isVisible === false} />

      <Drawer
        title={t('MENU')}
        placement="right"
        closable={true}
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        width={300}
        styles={{ body: { padding: '16px' } }}>
        <Nav onSelect={setActiveKey} vertical>
          <MobileNavItem
            eventKey="1"
            onClick={() => {
              push(navigateLink(AppRoutes.OFFERS));
              setDrawerVisible(false);
            }}>
            {t('OFFERS')}
          </MobileNavItem>
          <MobileNavItem
            eventKey="2"
            onClick={() => {
              push(navigateLink(AppRoutes.ADD_OFFER));
              setDrawerVisible(false);
            }}>
            {t('ADD_OFFER')}
          </MobileNavItem>
          <MobileNavItem
            eventKey="3"
            onClick={() => {
              push(navigateLink(AppRoutes.WANTED_LISTINGS));
              setDrawerVisible(false);
            }}>
            {t('WANTED_BOARD')}
          </MobileNavItem>
          {isAuth ? (
            <>
              <MobileNavItem
                onClick={(e) => {
                  handleClickMojaTablica(e as any);
                  setDrawerVisible(false);
                }}
                active={pathname.indexOf(AppRoutes.MOJA_TABLICA) > -1}>
                {t('LINK_MY_DASHBOARD')}
              </MobileNavItem>
              <MobileNavItem
                onClick={() => {
                  onSignOut();
                  setDrawerVisible(false);
                }}
                icon={<Icon icon="sign-out" />}>
                {t('LINK_LOG_OUT')}
              </MobileNavItem>
            </>
          ) : (
            <>
              <MobileNavItem
                icon={<Icon icon="user-plus" />}
                onClick={() => {
                  showSignUpModal();
                  gtm('click_button_form_reg');
                  setDrawerVisible(false);
                }}
                id="button_form_reg_mobile">
                {t('LINK_SING_UP')}
              </MobileNavItem>
              <MobileNavItem
                icon={<Icon icon="sign-in" />}
                onClick={() => {
                  showSignInModal();
                  setDrawerVisible(false);
                }}>
                {t('LINK_SING_IN')}
              </MobileNavItem>
            </>
          )}
        </Nav>
      </Drawer>

      <StyledNavbar
        appearance={isVisible === true ? 'inverse' : undefined}
        sticky={isVisible === false ? true : undefined}>
        <WidthWrapper>
          <StyledNavbarHeader>
            <Logo onClick={() => push(navigateLink(AppRoutes.HOME))} isVisible={isVisible} />
            <Navbar.Body>
              {isMobile && (
                <MenuButton sticky={isVisible === false ? true : undefined}>
                  <Nav pullRight>
                    <Nav.Item onClick={() => setDrawerVisible(true)}>
                      <Icon icon="bars" size="2x" />
                    </Nav.Item>
                  </Nav>
                </MenuButton>
              )}
            </Navbar.Body>
          </StyledNavbarHeader>

          {!isMobile && (
            <Navbar.Body>
              <Nav onSelect={setActiveKey} activeKey={activeKay}>
                <Dropdown menu={{ items }} placement="bottomLeft" overlayStyle={{ paddingTop: 5 }}>
                  <Nav.Item
                    style={{ margin: '0 24px' }}
                    eventKey="1"
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      push(navigateLink(AppRoutes.OFFERS));
                    }}
                    active={pathname.indexOf(AppRoutes.OFFERS) > -1}>
                    {t('LINK_I_RENT')} <DownOutlined />
                  </Nav.Item>
                </Dropdown>

                <Nav.Item
                  eventKey="2"
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    setPromo(SPECIAL_OFFER_KEY, 'SHOW', { path: '/' });
                    onAddOffer();
                    gtm('add_offer_fixed_menu');
                  }}
                  active={pathname.indexOf(AppRoutes.ADD_OFFER.replace('/:id', '')) > -1}>
                  {t('LINK_I_AM_RENTING')}
                </Nav.Item>

                <Nav.Item
                  eventKey="3"
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    push(navigateLink(AppRoutes.WANTED_LISTINGS));
                  }}
                  active={pathname.indexOf(AppRoutes.WANTED_LISTINGS) > -1}>
                  {t('WANTED_BOARD')}
                </Nav.Item>
              </Nav>
              <Nav pullRight>
                {isAuth ? (
                  <>
                    <Nav.Item onClick={handleClickMojaTablica} active={pathname.indexOf(AppRoutes.MOJA_TABLICA) > -1}>
                      <div className="relative flex w-auto items-center h-full">
                        <div className="flex flex-col mr-1">
                          <div className="font-semibold mr-3">{t('LINK_MY_DASHBOARD')}</div>
                        </div>

                        <Avatar circle src={currentUser.photoURL ?? '/images/avatar/default.jpg'} />
                      </div>
                    </Nav.Item>
                    <Nav.Item onClick={() => onSignOut()} icon={<Icon icon="sign-out" />}>
                      {t('LINK_LOG_OUT')}
                    </Nav.Item>
                  </>
                ) : (
                  <>
                    <Nav.Item
                      icon={<Icon icon="user-plus" />}
                      onClick={() => {
                        showSignUpModal();
                        gtm('click_button_form_reg');
                      }}
                      id="button_form_reg">
                      {t('LINK_SING_UP')}
                    </Nav.Item>
                    <Nav.Item icon={<Icon icon="sign-in" />} onClick={() => showSignInModal()}>
                      {t('LINK_SING_IN')}
                    </Nav.Item>
                  </>
                )}
                <Nav pullRight>
                  <LanguageSwitcher languages={['pl', 'en']} />
                </Nav>
              </Nav>
            </Navbar.Body>
          )}
        </WidthWrapper>
      </StyledNavbar>
    </Header>
  );
};
